import "@babylonjs/loaders/glTF";
import {
  Engine,
  Scene,
  SceneLoader,
  PointerEventTypes,
  Color3,
  Vector3,
  ArcRotateCamera,
  HemisphericLight,
  InstancedMesh
} from "@babylonjs/core";
// const PubSub = require("pubsub-js")
import PubSub from "pubsub-js";
import fs from 'fs';

// subscribes to:selectRow, deselectRow
// publishes: selectPartKey, deselectPartKey
// after instantiation, call create(element) to add to dom

export default class BdsGltfViewer {
  canvas: HTMLCanvasElement;
  engine: Engine;
  highlightColor: Color3;
  hostRelativeUri: string;
  scene: Scene;
  selectedPartKeys: Array<string>;
  selectFunction: string;
  mouseWheelPrecision: string;
  camera: ArcRotateCamera;
  controls: Array<HTMLInputElement>;

  constructor(hostRelativeUri: string) {
    this.hostRelativeUri = hostRelativeUri;

    this.canvas = this.createCanvas();
    this.engine = new Engine(this.canvas, true, { stencil: true });
    this.highlightColor = Color3.Red();
    this.scene = new Scene(this.engine);
    this.selectedPartKeys = [];
    this.selectFunction = "select";
    this.mouseWheelPrecision = "25";
    this.camera = this.createCamera();

    this.controls = [];

    // this.createCamera()
    this.createLight();
    this.createSubscriptions();

    // console.log('Attempting to load hostRelativeUri: ', this.hostRelativeUri)
    // console.log('cwd', window.location.pathname)

    // XDocs API
    SceneLoader.ImportMeshAsync(
      undefined,
      this.hostRelativeUri,
      undefined,
      this.scene,
      this.onImportSceneSuccess(),
      '.glb'
    );

    // this.runEngineRenderLoop();
  }

  addSelectedPartKey(partKey: string) {
    // console.log("adding", partKey)
    this.selectedPartKeys.push(partKey);
    // console.log("partKeys in array: ", this.selectedPartKeys);
  }

  clearAllSelectedPartKeys() {
    while (this.selectedPartKeys.length > 0) {
      this.deselectPartKey(this.selectedPartKeys[0]);
      // this.restorePartKey(this.selectedPartKeys[0])
    }
    // console.log("after clear: ", this.selectedPartKeys);
  }

  // preventScroll(e) {
  //   e.preventDefault();
  //   e.stopPropagation();

  //   return false;
  // }

  create(parentElement: HTMLElement = undefined) {
    // create() {
    const container = document.createElement("div");
    container.id = "bds-gltf-viewer-container";






    const clearButton = document.createElement("button");
    clearButton.innerHTML = "Reset";
    clearButton.addEventListener("click", () => {
      this.runEngineRenderLoop();
      this.clearAllSelectedPartKeys();
    });

    const selectPointerSelector = document.createElement("input");
    selectPointerSelector.type = "radio";
    selectPointerSelector.id = "select-pointer";
    selectPointerSelector.name = "pointer-type";
    selectPointerSelector.value = "select";
    selectPointerSelector.checked = true;
    this.controls.push(selectPointerSelector);

    const selectPointerLabel = document.createElement("label");
    selectPointerLabel.htmlFor = "select-pointer";
    selectPointerLabel.innerHTML = "Select";

    const selectPointerContainer = document.createElement("div");
    selectPointerContainer.appendChild(selectPointerLabel);
    selectPointerContainer.appendChild(selectPointerSelector);

    const removePointerSelector = document.createElement("input");
    removePointerSelector.type = "radio";
    removePointerSelector.id = "remove-pointer";
    removePointerSelector.name = "pointer-type";
    removePointerSelector.value = "remove";
    this.controls.push(removePointerSelector);

    const removePointerLabel = document.createElement("label");
    removePointerLabel.htmlFor = "remove-pointer";
    removePointerLabel.innerHTML = "Hide";

    const removePointerContainer = document.createElement("div");
    removePointerContainer.appendChild(removePointerLabel);
    removePointerContainer.appendChild(removePointerSelector);

    const pointerSelectorContainer = document.createElement("div");
    pointerSelectorContainer.id = "bds-gltf-viewer-pointer-selector-container";
    pointerSelectorContainer.setAttribute(
      "style",
      "min-width: 130px; display: flex; justify-content: space-between;"
    );

    pointerSelectorContainer.appendChild(selectPointerContainer);
    pointerSelectorContainer.appendChild(removePointerContainer);

    const wheelPrecisionSlider: HTMLInputElement = document.createElement(
      "input"
    );
    wheelPrecisionSlider.type = "range";
    wheelPrecisionSlider.id = "wheel-precision-slider";
    wheelPrecisionSlider.name = "wheel-precision-slider";
    wheelPrecisionSlider.min = "1";
    wheelPrecisionSlider.max = "80";
    wheelPrecisionSlider.value = this.mouseWheelPrecision;
    this.controls.push(wheelPrecisionSlider);

    const wheelPrecisionLabel = document.createElement("label");
    wheelPrecisionLabel.htmlFor = "wheel-precision-slider";
    wheelPrecisionLabel.innerHTML = "Mousewheel Precision";

    const wheelPrecisionContainer = document.createElement("div");
    wheelPrecisionContainer.appendChild(wheelPrecisionLabel);
    wheelPrecisionContainer.appendChild(wheelPrecisionSlider);

    const controlsContainer = document.createElement("div");
    controlsContainer.id = "bds-gltf-viewer-controls-container";
    controlsContainer.setAttribute(
      "style",
      "height: 3em; display: flex; justify-content: space-around; align-items: center;"
    );
    controlsContainer.appendChild(clearButton);

    controlsContainer.appendChild(pointerSelectorContainer);
    controlsContainer.appendChild(wheelPrecisionContainer);
    controlsContainer.addEventListener("click", () => {
      // console.log("clicked controls container");
      this.updateControls();
      // console.log("updateControls called, did it run?");
    });

    container.appendChild(controlsContainer);
    container.appendChild(this.canvas);

    // testing removing this to simply return
    if (parentElement) {
      parentElement.appendChild(container);
    }

    // moved to it's own function so it can be called after
    // connectedCallback in the web component
    // this.engine.runRenderLoop(() => {
    //   this.scene.render();
    // });
    // this.engine.resize();

    return container;
  }

  createCamera() {
    const camera: ArcRotateCamera = new ArcRotateCamera(
      "Camera",
      Math.PI / 2,
      Math.PI / 2,
      2,
      Vector3.Zero(),
      this.scene
    );
    camera.attachControl(this.canvas, true);
    camera.wheelPrecision = parseInt(this.mouseWheelPrecision);
    // camera.upperRadiusLimit = 10
    // camera.lowerRadiusLimit = 2
    // set initial camera view for 2CylinderEngine.glb
    camera.setPosition(new Vector3(0, 0, 600));
    return camera;
  }

  createCanvas() {
    const canvas = document.createElement("canvas");
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.overflow = "hidden";
    canvas.id = "bds-gltf-canvas";

    return canvas;
  }

  createLight() {
    const light: HemisphericLight = new HemisphericLight(
      "light1",
      new Vector3(1, 1, 0),
      this.scene
    );
    return light;
  }

  createSubscriptions() {
    PubSub.subscribe("selectRow", (msg: string, partKey: string) => {
      try {
        this.selectPartKey(partKey);
      } catch (e) {
        console.log(msg);
        console.error(e);
      }
    });
    PubSub.subscribe("deselectRow", (msg: string, partKey: string) => {
      try {
        this.deselectPartKey(partKey);
      } catch (e) {
        console.log(msg);
        console.error(e);
      }
    });
  }

  onImportSceneSuccess() {
    console.log('imported successfully')
    this.scene.onPointerObservable.add(pointerInfo => {
      if (
        pointerInfo.type === PointerEventTypes.POINTERDOWN &&
        pointerInfo.pickInfo.hit
      ) {
        const currentPart = pointerInfo.pickInfo.pickedMesh;
        if (currentPart.isAnInstance) {
          console.log("clicked instanced mesh");
          const sourceMesh = (currentPart as InstancedMesh).sourceMesh;
          this.onPartKeyClick(sourceMesh.id);
        } else {
          console.log("clicked mesh");
          this.onPartKeyClick(currentPart.id);
        }
      }
    });

    // this.runEngineRenderLoop()
    return undefined;
  }

  onPartKeyClick(partKey: string) {
    // PubSub.publish("onPartKeyClick", partKey)
    if (this.selectFunction === "remove") {
      this.removePartKey(partKey);
      return;
    }

    console.log("partKey", partKey);
    const part = this.getPart(partKey);
    // this.toggleHighlight(partKey)
    if (part.renderOverlay) {
      this.deselectPartKey(partKey);
    } else {
      this.selectPartKey(partKey);
    }
  }

  removeSelectedPartKey(partKey: string) {
    // console.log("removing", partKey)
    const partKeyToRemove = this.selectedPartKeys.indexOf(partKey);

    if (partKeyToRemove >= 0) {
      this.selectedPartKeys.splice(partKeyToRemove, 1);
    }
  }

  getPart(partKey: string) {
    return this.scene.getMeshByID(partKey);
  }

  selectPartKey(partKey: string) {
    console.log('selectPartKey() partkey: ', partKey)
    const part = this.getPart(partKey);
    console.log('is there a part? ', part)
    part.renderOverlay = true;
    part.overlayColor = this.highlightColor;
    PubSub.publish("selectPartKey", partKey);
    console.log("selectPartKey");
    this.addSelectedPartKey(partKey);
  }

  deselectPartKey(partKey: string) {
    const part = this.getPart(partKey);
    part.renderOverlay = false;
    part.isVisible = true;
    PubSub.publish("deselectPartKey", partKey);
    console.log("deselectPartKey");
    this.removeSelectedPartKey(partKey);
  }

  restorePartKey(partKey: string) {
    const part = this.getPart(partKey);
    part.isVisible = true;
    this.removeSelectedPartKey(partKey);
  }

  removePartKey(partKey: string) {
    const part = this.getPart(partKey);
    console.log("part", part);
    this.addSelectedPartKey(partKey);
    part.isVisible = false;
  }

  runEngineRenderLoop() {
    this.engine.runRenderLoop(() => {
      this.scene.render();
    });
    this.engine.resize();
  }

  updateControls() {
    this.controls.forEach(control => {
      console.log(control.value);
      if (control.checked) {
        this.selectFunction = control.value;
      }
      if (control.type === "range") {
        this.mouseWheelPrecision = control.value;
        this.camera.wheelPrecision = parseInt(this.mouseWheelPrecision);
      }
    });
  }
}
